/**
 * The fruit (spec 02 §7.3): peach, banana, strawberry, cherries, orange, watermelon, pineapple,
 * persimmon, grapes. Arcade colours, bold shapes: each reads by silhouette and one main colour.
 */

import { type Color, Vector3 } from 'three';
import { type MeshBuilder, place } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';
import { arcPts, type Builder, extrude, lathe, leaf, rotZ, tube } from './kit';

type Pt = readonly [number, number];

/** A round profile: an ellipse from the bottom pole to the top pole, centred at height `cy`. */
function roundProfile(r: number, cy: number, ry: number, n = 12): Pt[] {
  return arcPts(0, cy, r, ry, -Math.PI / 2, Math.PI / 2, n).map(([x, y]) => [Math.max(0, x), y] as const);
}

/** The radius of a lathe profile at height `y` (linear between points). */
function radiusAt(profile: readonly Pt[], y: number): number {
  for (let i = 0; i + 1 < profile.length; i++) {
    const [r0, y0] = profile[i] ?? [0, 0];
    const [r1, y1] = profile[i + 1] ?? [0, 0];
    if ((y >= y0 && y <= y1) || (y <= y0 && y >= y1)) return y1 === y0 ? r0 : r0 + ((r1 - r0) * (y - y0)) / (y1 - y0);
  }
  return 0;
}

/** A stalk with a leaf, for the round fruit. */
function stalk(mb: MeshBuilder, p: Palette, x: number, y: number, lean: number): void {
  const pts = [new Vector3(x, y - 0.06, 0), new Vector3(x + lean * 0.04, y + 0.06, 0),
    new Vector3(x + lean * 0.1, y + 0.14, 0)];
  tube(mb, pts, [0.035, 0.03, 0.025], p.color('stalk.1'), 6);
}

function banana(mb: MeshBuilder, c: Color, tip: Color, z: number, roll: number, lift: number): void {
  const pts: Vector3[] = [];
  const radii: number[] = [];
  const n = 12;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const a = Math.PI * (1.12 + 0.76 * t);
    pts.push(new Vector3(Math.cos(a) * 0.62, 0.86 + Math.sin(a) * 0.62 + lift, z));
    radii.push(Math.max(0.035, 0.15 * Math.pow(Math.sin(Math.PI * Math.min(1, t * 1.08)), 0.55)));
  }
  mb.with(rotZ(0, 0, roll), () => {
    tube(mb, pts, radii, c, 10);
    const end = pts[n] ?? new Vector3();
    const start = pts[0] ?? new Vector3();
    tube(mb, [end, end.clone().add(new Vector3(0.08, 0.06, 0))], [0.045, 0.04], tip, 6);
    mb.blob(0.04, 0.04, 0.04, tip, place(start.x - 0.01, start.y, start.z), { detail: 1 });
  });
}

export const FRUIT: Record<string, Builder> = {
  peach: (mb, p) => {
    // Round with a point on top, blushing rose above and cream below, a crease down the front.
    const prof: Pt[] = [...roundProfile(0.43, 0.44, 0.42, 10).slice(0, 9), [0.16, 0.86], [0.05, 0.94], [0, 0.96]];
    lathe(mb, prof, (seg) => p.color(seg < 4 ? 'peach.2' : 'peach.1'), undefined, { sides: 22 });
    const crease: Vector3[] = [];
    for (let i = 0; i <= 6; i++) {
      const y = 0.9 - i * 0.09;
      const r = radiusAt(prof, y) + 0.005;
      crease.push(new Vector3(Math.sin(0.35) * r, y, Math.cos(0.35) * r));
    }
    tube(mb, crease, [0.012, 0.022, 0.026, 0.026, 0.024, 0.018, 0.01], p.color('peach.0'), 6);
    leaf(mb, p.color('leaf.1'), 0.02, 0.9, 0.02, 0.36, 0.2, -1.05, 0.3);
    leaf(mb, p.color('leaf.2'), -0.02, 0.9, 0, 0.28, 0.16, 0.9, -0.3);
  },
  banana: (mb, p) => {
    banana(mb, p.color('banana.1'), p.color('stalk.0'), -0.1, 0.18, 0.06);
    banana(mb, p.color('banana.2'), p.color('stalk.0'), 0.08, 0.3, -0.04);
  },
  strawberry: (mb, p) => {
    const prof: Pt[] = [[0, 0.02], [0.1, 0.07], [0.22, 0.2], [0.33, 0.38], [0.39, 0.54], [0.39, 0.66], [0.33, 0.76],
      [0.2, 0.82], [0, 0.84]];
    lathe(mb, prof, p.color('berry.1'), undefined, { sides: 22 });
    // Seeds on the side toward the camera.
    for (const [y, a] of [[0.22, -0.5], [0.24, 0.6], [0.36, 0], [0.4, -1.1], [0.42, 1.0], [0.52, -0.5], [0.54, 0.5],
      [0.62, -1.3], [0.64, 1.2], [0.66, 0], [0.74, -0.6], [0.74, 0.7], [0.3, 1.5], [0.28, -1.6]] as const) {
      const r = radiusAt(prof, y);
      mb.blob(0.025, 0.035, 0.02, p.color('seed.1'), place(Math.sin(a) * r, y, Math.cos(a) * r, a), { detail: 1 });
    }
    // The green calyx, spread flat over the top, and its stem.
    for (let i = 0; i < 6; i++) {
      leaf(mb, p.color(i % 2 ? 'leaf.1' : 'leaf.2'), 0, 0.8, 0, 0.3, 0.14, 1.25, (i / 6) * Math.PI * 2 + 0.3);
    }
    tube(mb, [new Vector3(0, 0.8, 0), new Vector3(0.02, 0.92, 0), new Vector3(0.07, 0.98, 0)], [0.035, 0.03, 0.025],
      p.color('leaf.0'), 6);
  },
  cherries: (mb, p) => {
    const top = new Vector3(0.06, 0.98, -0.02);
    for (const [x, y, z] of [[-0.25, 0.24, 0.06], [0.25, 0.26, -0.06]] as const) {
      mb.blob(0.25, 0.24, 0.24, p.color('berry.1'), place(x, y, z), { detail: 3 });
      mb.blob(0.06, 0.05, 0.03, p.color('berry.2'), place(x - 0.1, y + 0.1, z + 0.2), { detail: 1 });
      const mid = new Vector3(x * 0.55 + 0.02, 0.66, z * 0.5);
      tube(mb, [new Vector3(x, y + 0.2, z), mid, top], [0.03, 0.026, 0.024], p.color('stalk.1'), 6);
    }
    leaf(mb, p.color('leaf.1'), top.x, top.y, top.z, 0.34, 0.18, -1.2);
  },
  orange: (mb, p) => {
    lathe(mb, roundProfile(0.45, 0.44, 0.43, 12), (seg) => p.color(seg > 8 ? 'orange.2' : 'orange.1'), undefined,
      { sides: 24 });
    mb.blob(0.05, 0.02, 0.05, p.color('orange.0'), place(0, 0.87, 0), { detail: 1 });
    stalk(mb, p, 0, 0.9, 1);
    leaf(mb, p.color('leaf.1'), 0.03, 0.9, 0.02, 0.36, 0.2, -1.15, 0.2);
  },
  watermelon: (mb, p) => {
    // A half-round slice, flat edge up: green rind, a white band, red flesh with black seeds.
    const slice = (r: number): Pt[] => [...arcPts(0, 0.62, r, r * 0.98, Math.PI, Math.PI * 2, 16)];
    extrude(mb, slice(0.6), 0.34, (i) => p.color(i % 3 === 0 ? 'melon.0' : 'melon.1'), undefined, p.color('melon.1'));
    extrude(mb, slice(0.53), 0.37, p.color('rind.1'), undefined, p.color('rind.1'));
    extrude(mb, slice(0.47), 0.4, p.color('berry.1'), undefined, p.color('berry.1'));
    for (const [x, y] of [[-0.28, 0.5], [0, 0.44], [0.28, 0.5], [-0.14, 0.3], [0.14, 0.3], [0, 0.18], [-0.34, 0.36],
      [0.34, 0.36]] as const) {
      mb.blob(0.035, 0.055, 0.02, p.color('eye.0'), place(x, y, 0.2), { detail: 1 });
    }
  },
  pineapple: (mb, p) => {
    // Diamond-cut skin: each ring of the lathe turned half a cell, two golds in a checker.
    const prof = roundProfile(0.31, 0.38, 0.37, 10);
    const sides = 12;
    lathe(mb, prof, (seg, k) => p.color((seg + k) % 2 ? 'pineapple.1' : 'pineapple.2'), undefined, {
      sides, twist: prof.map((_, i) => (i * Math.PI) / sides),
    });
    const crown: Array<[number, number, number]> = [[0, 0.42, 0], [0.45, 0.36, 0.2], [-0.45, 0.36, -0.2],
      [0.85, 0.28, 1.2], [-0.85, 0.28, -1.2], [0.25, 0.3, 2.6], [-0.25, 0.3, -2.6]];
    for (const [roll, len, yaw] of crown) {
      leaf(mb, p.color(Math.abs(roll) > 0.5 ? 'leaf.1' : 'leaf.2'), 0, 0.7, 0, len, 0.14, roll, yaw);
    }
  },
  persimmon: (mb, p) => {
    // Squat and deep orange-red under a big four-leaf calyx.
    lathe(mb, [[0, 0.03], [0.24, 0.02], [0.4, 0.1], [0.47, 0.24], [0.47, 0.4], [0.42, 0.54], [0.3, 0.63], [0.12, 0.66],
      [0, 0.64]], (seg) => p.color(seg > 5 ? 'persimmon.2' : 'persimmon.1'), undefined, { sides: 24 });
    for (let i = 0; i < 4; i++) {
      leaf(mb, p.color(i % 2 ? 'leaf.0' : 'leaf.1'), 0, 0.64, 0, 0.3, 0.2, 1.3, (i / 4) * Math.PI * 2 + 0.4);
    }
    tube(mb, [new Vector3(0, 0.62, 0), new Vector3(0, 0.76, 0)], [0.045, 0.04], p.color('stalk.1'), 6);
  },
  grapes: (mb, p) => {
    // A bunch widest at the top, hanging from a stalk with a leaf.
    const rows: ReadonlyArray<readonly [number, number]> = [[0.84, 4], [0.65, 3], [0.46, 3], [0.29, 2], [0.13, 1]];
    rows.forEach(([y, n], row) => {
      for (let i = 0; i < n; i++) {
        const x = (i - (n - 1) / 2) * 0.22;
        const z = ((i + row) % 2 ? -0.06 : 0.06);
        mb.blob(0.13, 0.13, 0.13, p.color('grape.1'), place(x, y, z), { detail: 2, topColor: p.color('grape.2') });
      }
    });
    tube(mb, [new Vector3(0, 0.9, 0), new Vector3(0.02, 1.02, 0), new Vector3(0.1, 1.08, 0)], [0.035, 0.03, 0.025],
      p.color('stalk.1'), 6);
    leaf(mb, p.color('leaf.1'), 0.02, 0.98, 0.04, 0.4, 0.26, 1.2);
  },
};
